import React from 'react';

// ─── Estado según porcentaje ───────────────────────────────────────────────────
function statusOf(pct) {
    if (pct >= 100) return { color: '#3275b4', label: 'Excedente' };
    if (pct >= 60)  return { color: '#27762F', label: 'Óptimo'    };
    if (pct >= 30)  return { color: '#a98000', label: 'Moderado'  };
    return                 { color: '#bd413f', label: 'Crítico'   };
}

// ─── Fila de dato individual ───────────────────────────────────────────────────
const DetRow = ({ label, value }) => {
    if (value === undefined || value === null || value === '') return null;
    return (
        <div className="presa-detalle-row">
            <span className="presa-detalle-row-label">{label}</span>
            <span className="presa-detalle-row-value">{value}</span>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// GRÁFICA COMPARATIVA — LÍNEA CON PUNTOS
// ═══════════════════════════════════════════════════════════════════════════════

// Path SVG con curvas cúbicas suaves entre puntos
function smoothPath(pts) {
    if (!pts || pts.length < 2) return '';
    let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
        const [px, py] = pts[i - 1];
        const [cx, cy] = pts[i];
        const cpx = (cx - px) * 0.4;
        d += ` C${(px + cpx).toFixed(1)},${py.toFixed(1)} ${(cx - cpx).toFixed(1)},${cy.toFixed(1)} ${cx.toFixed(1)},${cy.toFixed(1)}`;
    }
    return d;
}

const LINE_COLOR = '#27762F';

const GraficaComparativo = ({ series }) => {
    if (!series || series.length === 0) return null;

    // Ordenar cronológicamente para el eje X: 2024 → 2025 → 2026
    const ordered = [...series].sort((a, b) => parseInt(a.año) - parseInt(b.año));

    const W = 380, H = 120;
    const padL = 28, padR = 12, padT = 28, padB = 24;
    const innerW = W - padL - padR;
    const innerH = H - padT - padB;

    const maxVal = Math.max(100, ...ordered.map(s => s.pct));
    const yBase  = padT + innerH;
    const yOf    = (v) => padT + innerH - (v / maxVal) * innerH;
    const xOf    = (i) => padL + (i / Math.max(ordered.length - 1, 1)) * innerW;

    const pts      = ordered.map((s, i) => [xOf(i), yOf(s.pct)]);
    const linePath = smoothPath(pts);
    const last     = pts[pts.length - 1];
    const first    = pts[0];
    const areaPath = `${linePath} L${last[0].toFixed(1)},${yBase.toFixed(1)} L${first[0].toFixed(1)},${yBase.toFixed(1)} Z`;

    return (
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="presa-grafica-svg">
            <defs>
                <linearGradient id="pgAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={LINE_COLOR} stopOpacity="0.18" />
                    <stop offset="100%" stopColor={LINE_COLOR} stopOpacity="0"    />
                </linearGradient>
            </defs>

            {/* Grid horizontal */}
            {[0, 25, 50, 75, 100].map(v => (
                <line key={v}
                    x1={padL} y1={yOf(v)} x2={W - padR} y2={yOf(v)}
                    stroke="#e5e7eb"
                    strokeWidth={v === 0 ? 1 : 0.7}
                    strokeDasharray={v === 0 ? 'none' : '3,3'}
                />
            ))}

            {/* Etiquetas eje Y */}
            {[0, 50, 100].map(v => (
                <text key={v}
                    x={padL - 4} y={yOf(v) + 3}
                    fontSize="7.5" fill="#b0b7c3" textAnchor="end"
                >{v}%</text>
            ))}

            {/* Área rellena bajo la línea */}
            <path d={areaPath} fill="url(#pgAreaGrad)" />

            {/* Línea principal */}
            <path d={linePath}
                fill="none" stroke={LINE_COLOR}
                strokeWidth="2.2"
                strokeLinejoin="round" strokeLinecap="round"
            />

            {/* Puntos + etiquetas por año */}
            {ordered.map((s, i) => {
                const cx = xOf(i);
                const cy = yOf(s.pct);
                const isLatest = i === ordered.length - 1;
                return (
                    <g key={s.año}>
                        {/* Círculo */}
                        <circle cx={cx} cy={cy}
                            r={isLatest ? 4.5 : 3.5}
                            fill="#fff" stroke={LINE_COLOR} strokeWidth="2.2"
                        />
                        {/* Porcentaje encima */}
                        <text x={cx} y={cy - 9}
                            fontSize="9" fontWeight="700"
                            fill={LINE_COLOR} textAnchor="middle"
                        >{s.pct.toFixed(1)}%</text>
                        {/* Año debajo del eje */}
                        <text x={cx} y={H - 7}
                            fontSize="9"
                            fontWeight={isLatest ? '700' : '500'}
                            fill={isLatest ? '#374151' : '#9ca3af'}
                            textAnchor="middle"
                        >{s.año}</text>
                    </g>
                );
            })}
        </svg>
    );
};

// Construye series a partir del historial
function buildSeries(historial) {
    return historial.map((h, i) => ({
        id:  `ps-${h.año || i}`,
        año: h.año,
        pct: parseFloat(h.presa?.porcentaje ?? 0),
    }));
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

const DashPresaDetalle = ({ presa, presaKey, historial, totalPresa, totalHistorial }) => {
    // Si no hay presa seleccionada usa el total (promedio general)
    const efectivaPresa     = presa     || totalPresa;
    const efectivoHistorial = presa     ? historial : (totalHistorial || []);
    const esTotal           = !presa && !!totalPresa;

    // ── Estado vacío: ni presa ni total disponible ────────────────────────
    if (!efectivaPresa) {
        return (
            <div className="presa-detalle-empty">
                <svg className="presa-detalle-empty-svg" width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <circle cx="22" cy="22" r="20" stroke="#e0e4ea" strokeWidth="2" />
                    <path d="M22 14v9M22 27v2" stroke="#c8cdd6" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
                <div className="presa-detalle-empty-title">Sin selección</div>
                <div className="presa-detalle-empty-sub">
                    Haz clic en un pin del mapa o en una presa del listado para ver su detalle
                </div>
            </div>
        );
    }

    const pct     = parseFloat(efectivaPresa.porcentaje ?? 0);
    const fillPct = Math.min(pct, 100);
    const { color, label } = statusOf(pct);

    const series = efectivoHistorial.length > 0 ? buildSeries(efectivoHistorial) : [];

    return (
        <div className="presa-detalle">

            {/* ── Header: nombre + badge de estado ─────────────────────────── */}
            <div className="presa-detalle-header" style={{ borderLeftColor: color }}>
                <div className="presa-detalle-nombre">
                    {esTotal ? (efectivaPresa.presa || 'Promedio general') : (efectivaPresa.presa || presaKey)}
                </div>
                <span className="presa-detalle-badge" style={{ background: color }}>{label}</span>
            </div>

            {/* ── Indicador principal ───────────────────────────────────────── */}
            <div className="presa-detalle-gauge">
                <div className="presa-detalle-gauge-row">

                    {/* Número grande */}
                    <div className="presa-detalle-gauge-left">
                        <div className="presa-detalle-gauge-number" style={{ color }}>
                            {pct.toFixed(1)}
                            <span className="presa-detalle-gauge-unit">%</span>
                        </div>
                        <div className="presa-detalle-gauge-sublabel">Almacenamiento actual</div>
                    </div>

                    {/* Mini depósito visual */}
                    <div className="presa-detalle-tank-wrap">
                        <div className="presa-detalle-tank">
                            <div
                                className="presa-detalle-tank-water"
                                style={{ height: `${fillPct}%`, background: color }}
                            />
                        </div>
                        <div className="presa-detalle-tank-label">Nivel</div>
                    </div>
                </div>

                {/* Barra de progreso */}
                <div className="presa-detalle-bar-track">
                    <div
                        className="presa-detalle-bar-fill"
                        style={{ width: `${fillPct}%`, background: color }}
                    />
                </div>
                <div className="presa-detalle-bar-ticks">
                    <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                </div>
            </div>

            {/* ── Gráfica comparativa de años ───────────────────────────────── */}
            {series.length > 0 && (
                <div className="presa-grafica-container">
                    <div className="presa-grafica-titulo">Comparativo anual</div>
                    <GraficaComparativo series={series} />
                </div>
            )}

            {/* ── Riesgo y Pronóstico ───────────────────────────────────────── */}
            {/* {(presa.riesgo_principal || presa.pronostico) && (
                <div className="presa-detalle-alertas">
                    {presa.riesgo_principal && (
                        <div className="presa-detalle-alerta presa-detalle-alerta--riesgo">
                            <div className="presa-detalle-alerta-header">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2L1 21h22L12 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                                    <path d="M12 9v5M12 17v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                                <span>Riesgo principal</span>
                            </div>
                            <div className="presa-detalle-alerta-texto">{presa.riesgo_principal}</div>
                        </div>
                    )}
                    {presa.pronostico && (
                        <div className="presa-detalle-alerta presa-detalle-alerta--pronostico">
                            <div className="presa-detalle-alerta-header">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>Pronóstico</span>
                            </div>
                            <div className="presa-detalle-alerta-texto">{presa.pronostico}</div>
                        </div>
                    )}
                </div>
            )} */}

            {/* ── Datos de la presa ─────────────────────────────────────────── */}
            {/* <div className="presa-detalle-section-label">Datos de la presa</div>
            <div className="presa-detalle-rows scroll">
                <DetRow label="Actualización" value={presa.fechaactualizacion} />
                <DetRow label="Monitoreo"     value={presa.fechamonitoreo} />
                <DetRow label="Elevación"     value={presa.elevacionactual != null ? `${presa.elevacionactual} msnm` : null} />
                <DetRow label="Almacenamiento" value={presa.almacenaactual != null ? `${presa.almacenaactual} hm³` : null} />
                <DetRow label="Corriente"     value={presa.corriente} />
                <DetRow label="Municipio"     value={presa.nommunicipio} />
                <DetRow label="Estado"        value={presa.estado} />
                <DetRow label="Región"        value={presa.regioncna} />
            </div> */}
        </div>
    );
};

export default DashPresaDetalle;
